define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi/_base/storage",
	"csi/_base/underscore",
	"csi/core/Key",
	"csi/expander/Widget",
	"csi/middleware/attributes/attributeKeysService",
	"csi/store/Nodes",
	"csi/table/Widget",
	"csi/table/Preferences",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"./SecondaryDisplay/SecondaryDisplayCustomViewOptionsStore",
	"./SecondaryDisplay/SecondaryDisplayOptionsTab",
	"./VersionView",
	"./VersionTableToolbarPluginChart",
	"Merchandising/SecondaryDisplay/CustomViewSecondaryDisplayOptions",
], function(declare, lang, storage, _, Key, Widget, {removeMatrixFromAttributeKey}, Nodes, TableWidget, Preferences, xml, Query, QueryProcessor,
	SecondaryDisplayCustomViewOptionsStore, SecondaryDisplayOptionsTab, VersionView, VersionTableToolbarPluginChart, CustomViewSecondaryDisplayOptions) {

	const _Preferences = declare(Preferences, {
		constructor: function() {
			this.addCustomOption("secondaryDisplayType", CustomViewSecondaryDisplayOptions.SecondaryDisplayType.SECONDARY_DISPLAY_TYPE_COLUMN, false, "SecondaryDisplayType");
		}
	});

	return declare([Widget, TableWidget, VersionView], {
		SECONDARY_TYPE_STORAGE_KEY: "MerchSecondaryTypeURL",
		SECONDARY_FILTER_LEVEL: "1",

		// reflist: array of references to active MerchSecondaryVersions
		activeMerchSecondaries: null,

		// ref: active secondary type (MerchSecondaryPlanSubtype)
		activeMerchSecondaryType: null,

		/** @property {boolean} hasMatrixHeading - indicates if a secondary matrix is available in the view */
		hasMatrixHeading: null,

		// int: secondary display type (0,1,2) for disable, column or row
		secondaryDisplayType: null,

		// ref: secondary type (MerchSecondaryPlanSubtype)
		urlSecondaryType: null,

		pluginClasses: lang.mixin({
			charts: VersionTableToolbarPluginChart
		}, Widget.prototype.pluginClasses),

		getPrintProperties: function() {
			// summary: add secondary type to print properties
			var params = this.inherited(arguments);
			params.activeMerchSecondaries = this.activeMerchSecondaries;
			params.activeMerchSecondaryType = this.activeMerchSecondaryType;
			params.urlSecondaryType = this.urlSecondaryType;
			return params;
		},

		getStorageKey: function(secType) {
			var level = this._urlLevel || this.urlRoot;
			if (!level) {
				return null; // no plan
			}
			var key = this.SECONDARY_TYPE_STORAGE_KEY + "_" + level;
			if (secType)
				key += "_" + secType;
			return key;
		},

		/**
		 * Return true if the custom view option for secondary display type is not 0 (disabled) and the matrix is selected
		 * @return {boolean}
		 */
		isShowingSecondaries() {
			const {SECONDARY_DISPLAY_DISABLE, SECONDARY_DISPLAY_TYPE_COLUMN} = CustomViewSecondaryDisplayOptions.SecondaryDisplayType;
			const {attributeSpecs, secondaryDisplayType} = this;

			if (secondaryDisplayType == SECONDARY_DISPLAY_DISABLE) {
				return false;
			} else if (secondaryDisplayType == SECONDARY_DISPLAY_TYPE_COLUMN) {
				return !!attributeSpecs?.find({id: "Secondaries"});
			}

			return true;
		},

		onViewCreate: function() {
			var typeKey = null;
			var key = this.getStorageKey();

			if (key) {
				var secType = storage.get(key) || storage.get("MerchSecondaryPlanSubtype");

				this.watch("activeMerchSecondaries", function(name, oldValue, newValue) {
					if (typeKey) {
						storage.set(typeKey, newValue);
					}
				});

				this.watch("activeMerchSecondaryType", function(name, oldValue, newValue) {
					secType = newValue;
					storage.set(key, secType);
					storage.set("MerchSecondaryPlanSubtype", secType);
					typeKey = this.getStorageKey(secType);
					if (typeKey) {
						var secs = storage.get(typeKey);
						this.set("activeMerchSecondaries", secs);
					}
				});

				if (secType) {
					this.set("activeMerchSecondaryType", secType);
				}
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Return dropdown options for secondary display type
		 */
		getCustomViewDropDownOptions() {
			var options = this.inherited(arguments);

			// Secondary display type dropdown options
			const secondaryDisplayTypeOptions = [
				{id: CustomViewSecondaryDisplayOptions.SecondaryDisplayType.SECONDARY_DISPLAY_DISABLE, res: "SecondaryDisable"},
				{id: CustomViewSecondaryDisplayOptions.SecondaryDisplayType.SECONDARY_DISPLAY_TYPE_COLUMN, res: "SecondaryDisplayColumn"},
				{id: CustomViewSecondaryDisplayOptions.SecondaryDisplayType.SECONDARY_DISPLAY_TYPE_ROW, res: "SecondaryDisplayRow"}
			];

			return {
				...options,
				secondaryDisplayTypeOptions,
			};
		},

		_resolveView: function() {
			const hasSecondaries = this.hasMatrixHeading = (this.defView.findHeading("Secondaries") != null);
			if (hasSecondaries) {
				this.prefClass = _Preferences;
				this.customViewOptionsComponent = SecondaryDisplayOptionsTab;
				this.customViewOptionsStore = SecondaryDisplayCustomViewOptionsStore;
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Replace secondary matrix group key with an attribute group when showing secondaries 
		 */
		onViewTransformVisibleKeys(visibleKeys) {
			const {SECONDARY_DISPLAY_DISABLE, SECONDARY_DISPLAY_TYPE_ROW} = CustomViewSecondaryDisplayOptions.SecondaryDisplayType;
			const {secondaryDisplayType} = this;

			return _.compact(visibleKeys.map((groupOrKey) => {
				if (groupOrKey.matrixTarget === "MerchSecondaryVersion") {
					if (secondaryDisplayType === SECONDARY_DISPLAY_TYPE_ROW) {
						// turn matrix into custom group
						return {
							keys: groupOrKey.keys.map((subKey) => removeMatrixFromAttributeKey(subKey, 1)),
							name: groupOrKey.name,
						};
					} else if (secondaryDisplayType === SECONDARY_DISPLAY_DISABLE) {
						return null;
					}
				}

				return groupOrKey;
			}));
		},

		_createSecondaryStore: function(/*Context*/ctx, /*Boolean?*/bConfigurable) {
			// if you are on a configurable view, ignore this
			if (bConfigurable) {
				return null;
			}

			// look at the intersection of secondaries according to the custom filter, and the secondaries selected on the switchboard
			// if the intersection is empty, just use the secondaries on the custom filter
			var allSecondaries = ctx.getNode().Secondaries;

			// set up query to filter on secondary type
			var queryObject = new Query().RefAttr("Subtype", xml.OP_EQUALS, this.urlSecondaryType, "Child:__Parent__");
			allSecondaries = _.map(QueryProcessor.applyQueryToNodes(queryObject, ctx.cache.getNodes(allSecondaries), ctx.cache), xml.nodeGetURL);

			var switchboardSecondaryURLs = ctx.view.get("activeMerchSecondaries"); // from switchboard
			if (switchboardSecondaryURLs == null) {
				// In case of ShowAllSecondaries boolean on plan is true then set secondaryNodes to all secondaries
				const view = ctx.view;
				const node = view.getViewNode();
				const cache = view.cacheView;
				const level = node && cache.get(node.__Parent__);
				const plan = level && cache.get(level.Plan);
				if (plan && plan.ShowAllSecondaries) {
					switchboardSecondaryURLs = allSecondaries;
				}
			}

			var switchboardSecondaryPlanSecondaries = [];
			_.each(switchboardSecondaryURLs, function(url) {
				var snode = ctx.cache.getNode(url);
				if (snode)
					switchboardSecondaryPlanSecondaries.push(snode.PlanSecondary);
			});
			var switchboardSecondaries = []

			_.each(allSecondaries, function(s) {
				var pn = ctx.cache.getNode(s);
				if (pn && _.contains(switchboardSecondaryPlanSecondaries, pn.PlanSecondary))
					switchboardSecondaries.push(s);
			});

			var ql = ctx.view.getQueryLevel(this.SECONDARY_FILTER_LEVEL);
			var qlf = ctx.view.getQueryLevelFilter(ql);
			if (qlf)
				queryObject = qlf.toQuery(queryObject); // add filter

			var filteredSecondaryNodes = (queryObject != null) ? QueryProcessor.applyQueryToNodes(queryObject, ctx.cache.getNodes(allSecondaries), ctx.cache) : ctx.cache.getNodes(allSecondaries);
			var filteredSecondaries = _.map(filteredSecondaryNodes, xml.nodeGetURL);
			var returnedSecondaries = _.intersection(switchboardSecondaries, filteredSecondaries);

			return new Nodes({
				cachingStore: ctx.cache,
				data: ctx.cache.getNodes(returnedSecondaries)
			});
		},

		_createQueryLevel: function(/*Object*/params, /*Boolean*/bRoot) {
			// summary: set up rows for secondaries to appear if the row option is set
			var ql = this.inherited(arguments);

			//Don't perform any filtering if we don't have the matrix
			if (!this.hasMatrixHeading) {
				return ql;
			}

			// don't show children if you are in "column" mode on Plan tab
			if (ql.bMain && this.secondaryDisplayType == CustomViewSecondaryDisplayOptions.SecondaryDisplayType.SECONDARY_DISPLAY_TYPE_COLUMN) {
				ql.hasChildrenTest = () => false;
			}

			//Don't create a "Secondaries" query level if disabled or there are no secondaries on this level
			if (ql.getLevelIndex() == this.SECONDARY_FILTER_LEVEL && this.secondaryDisplayType == CustomViewSecondaryDisplayOptions.SecondaryDisplayType.SECONDARY_DISPLAY_DISABLE) {
				return null;
			}

			if (ql.type == "MerchSecondaryVersion") {
				ql.createCustomStore = lang.hitch(this, this._createSecondaryStore);
			}

			return ql;
		},

		/**
		 * @override
		 * Add dependency path to the MerchLevel versions
		 * This path is needed to support the secondary switchboard.
		 */
		_onRootDependencyPaths: function(paths) {
			paths.push("Child:Versions");
			this.inherited(arguments);
		}
	});
});